import type { ISalesSummary, IServicingSummary, IComplianceSummary } from 'src/types/summary';

import { useMemo, useState, useContext, createContext } from 'react';

// Define the generic context type
interface SummaryContextType<T> {
  summary?: T;
  setSummary: React.Dispatch<React.SetStateAction<T | undefined>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create a generic context
const createSummaryContext = <T,>() => createContext<SummaryContextType<T> | undefined>(undefined);

// Create specific contexts for different summary types
export const ServicingSummaryContext = createSummaryContext<IServicingSummary>();
export const ComplianceSummaryContext = createSummaryContext<IComplianceSummary>();
export const SalesSummaryContext = createSummaryContext<ISalesSummary>();

// Generic provider component
export const SummaryContextProvider = <T,>({
  children,
  context,
}: React.PropsWithChildren<{ context: React.Context<SummaryContextType<T> | undefined> }>) => {
  const [summary, setSummary] = useState<T>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const summaryValues = useMemo(
    () => ({
      summary,
      setSummary,
      isLoading,
      setIsLoading,
    }),
    [summary, isLoading]
  );

  return <context.Provider value={summaryValues}>{children}</context.Provider>;
};

// Custom hooks for each context type
export const useServicingSummary = () => {
  const context = useContext(ServicingSummaryContext);
  if (context === undefined) {
    throw new Error('useServicingSummary must be used within a ServicingSummaryProvider');
  }
  return context;
};

export const useComplianceSummary = () => {
  const context = useContext(ComplianceSummaryContext);
  if (context === undefined) {
    throw new Error('useComplianceSummary must be used within a ComplianceSummaryProvider');
  }
  return context;
};

export const useSalesSummary = () => {
  const context = useContext(SalesSummaryContext);
  if (context === undefined) {
    throw new Error('useSalesSummary must be used within a SalesSummaryProvider');
  }
  return context;
};
